import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { CompanyContext } from '../../../../../context/CompanyContext';
import {
  CompanyMutations,
  UpdateCompanyResponse,
  UpdateCompanyVariables,
} from '../../../../../graphql/company.graphql';
import { CreateFilesResponse, CreateFilesVariables, FileMutations } from '../../../../../graphql/file.graphql';
import useMutation from '../../../../../hooks/useMutation';
import { useToast } from '../../../../../providers/ToastProvider';
import { LanguageUtils } from '../../../../../types/common/languageRegionDesignator';
import { Company } from '../../../../../types/company';
import { CreateFileInput, FileEntityType } from '../../../../../types/file';
import { TranslationKey } from '../../../../../types/translation/TranslationKey';
import { t } from '../../../../../types/translation/Translator';
import { ColorUtil } from '../../../../../util/color.util';
import { FileViewLoader } from '../../../../../VentoryUI/components/common/File';
import { Button, ButtonVariant } from '../../../../components/Button';
import { ColorPicker } from '../../../../components/ColorPicker';
import { Divider } from '../../../../components/Divider';
import { LanguageSelect } from '../../../../components/Dropdown';
import { TextInput } from '../../../../components/TextInput';
import { ToastType } from '../../../../components/Toast';
import { ReactComponent as VentoryWhiteLogo } from '../../../../../assets/img/Ventory-WhiteNoBack.svg';
import Icon, { IconType } from '../../../../icons/Icon';
import { LoadingSpinner } from '../../../../components/LoadingSpinner';
import { SuperUserContext } from '../../../../../context/SuperUserContext';
import { SuperUserRole } from '../../../../../types/superUser';
import { FileContext } from '../../../../../context/FileContext';
import SaveChangesButton from '../../../../components/Button/templates/SaveChangesButton';

export default function DisplaySettingsDetailPane() {
  const { showToast } = useToast();
  const inputRef = useRef<HTMLInputElement>(null);
  const { currentCompany, setCurrentCompany } = useContext(CompanyContext);
  const { superUser } = useContext(SuperUserContext);
  const { files, setFiles } = useContext(FileContext);

  const [company, setCompany] = useState(new Company(currentCompany));

  const hasChanges = useMemo(() => {
    if (company.name !== currentCompany.name) return true;
    if (company.settings.language !== currentCompany.settings.language) return true;
    if (company.settings.primaryColor !== currentCompany.settings.primaryColor) return true;
    if (company.settings.headerColor !== currentCompany.settings.headerColor) return true;
    if (company.settings.accentColor !== currentCompany.settings.accentColor) return true;
    if (company.settings.companyLogo !== currentCompany.settings.companyLogo) return true;
    return false;
  }, [company]);

  useEffect(() => {
    return () => {
      ColorUtil.setCompanyColor(document, currentCompany.settings);
    };
  }, [currentCompany]);

  const [updateCompany, { loading: companyUpdateLoading }] = useMutation<UpdateCompanyResponse, UpdateCompanyVariables>(
    CompanyMutations.update,
    {
      variables: {
        company: company.forUpdate(),
      },
      onCompleted: response => {
        setCurrentCompany(new Company(response.updateCompany));
        setCompany(new Company(response.updateCompany));
        showToast({
          type: ToastType.success,
          title: t(TranslationKey.companyUpdatedSuccessfullyToastTitle).singular.upper,
        });
      },
    },
  );

  const [createFile, { loading: createFileLoading }] = useMutation<CreateFilesResponse, CreateFilesVariables>(
    FileMutations.create,
  );

  const handleFileUpload = async (file: File) => {
    const input = await CreateFileInput.fromFile(file, currentCompany.id, FileEntityType.company, currentCompany.id);
    await createFile({
      variables: { files: [input] },
      onCompleted: response => {
        const createdFile = response.createFiles[0];
        setFiles(new Map(files).set(createdFile.id, createdFile));
        setCompany(company.withLogo(response.createFiles[0].id));
      },
    });
  };

  const handleInputChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.currentTarget.files?.length) handleFileUpload(event.currentTarget.files[0]);
    event.currentTarget.value = '';
  };

  const openFilePicker = () => {
    if (inputRef && inputRef.current) inputRef.current.click();
  };

  return (
    <div className='flex flex-grow flex-col gap-8 overflow-auto'>
      {/* <Banner title={'Make it yours with custom branding'} iconType={IconType.diamond1} button>
        <p>
          Personalized colors and your company logo are available with our Enterprise plan. Contact us to learn how you
          can tailor your account to match your brand.
        </p>
        <div className='flex justify-end'>
          <Button variant={ButtonVariant.info} onClick={() => {}} text='Request a trial' />
        </div>
      </Banner> */}

      <div className='grid gap-4'>
        <div className='r-grid'>
          <TextInput
            mandatory
            label={t(TranslationKey.companyName).singular.upper}
            value={company.name}
            onChange={value => setCompany(company.withName(value))}
          />
        </div>
        <div className='r-grid'>
          <LanguageSelect
            label
            tooltip={t(TranslationKey.displaySettingsLanguageInformation).singular.upper}
            language={LanguageUtils.translationLanguageFromStorageString(company.settings.language)}
            setLanguage={language => setCompany(company.withLanguage(language))}
          />
        </div>
      </div>

      <div className='pr-2'>
        <Divider />
      </div>

      <p className='section-title'>{t(TranslationKey.branding).singular.upper}</p>

      <div className='flex gap-4'>
        <div
          className='relative flex w-[224px] items-center justify-center border border-dashed border-gray-default bg-gray-default stroke-on-header p-2'
          style={{ backgroundColor: company.settings.headerColor }}
        >
          {createFileLoading ? (
            <div className='text-on-header'>
              <LoadingSpinner />
            </div>
          ) : company.settings.companyLogo ? (
            <>
              <Icon
                stroke={'stroke-on-header'}
                className='absolute right-0.5 top-0.5'
                onClick={() => setCompany(company.withLogo(undefined))}
                type={IconType.x}
              />
              <FileViewLoader fileEntityType={FileEntityType.company} fileId={company.settings.companyLogo!} />
            </>
          ) : (
            <VentoryWhiteLogo />
          )}
        </div>

        <div className='flex flex-col gap-1'>
          <p className='text-sm font-medium text-black'>
            {t(TranslationKey.displaySettingsLogoInformation).singular.upper}
          </p>
          <div className='subtitle'>
            <p>{t(TranslationKey.displaySettingsRecommendedLogoResolution).singular.upper}</p>
            <p>{t(TranslationKey.displaySettingsMaximumLogoFileSize).singular.upper}</p>
          </div>
          <input
            ref={inputRef}
            type='file'
            className='hidden'
            accept='.png,.jpg,.jpeg,.svg'
            onChange={handleInputChange}
          />
          <Button
            variant={ButtonVariant.linkPrimary}
            text={t(TranslationKey.changeLogo).singular.upper}
            onClick={openFilePicker}
            className='w-fit !p-0'
          />
        </div>
      </div>

      <div className='r-grid'>
        <ColorPicker
          defaultColor={currentCompany.settings.primaryColor}
          color={company.settings.primaryColor}
          label={t(TranslationKey.primaryColor).singular.upper}
          tooltip={t(TranslationKey.displaySettingsPrimaryColorInformation).singular.upper}
          onChange={value => {
            ColorUtil.setPrimaryColor(document, value);
            setCompany(company.withPrimaryColor(value));
          }}
        />
        <ColorPicker
          defaultColor={currentCompany.settings.headerColor}
          color={company.settings.headerColor}
          label={t(TranslationKey.headerBackground).singular.upper}
          tooltip={t(TranslationKey.displaySettingsHeaderColorInformation).singular.upper}
          onChange={value => {
            ColorUtil.setHeaderColor(document, value);
            setCompany(company.withHeaderColor(value));
          }}
        />
        {superUser?.role === SuperUserRole.administrator && (
          <ColorPicker
            defaultColor={currentCompany.settings.accentColor}
            color={company.settings.accentColor}
            label={t(TranslationKey.accentColor).singular.upper}
            onChange={value => {
              ColorUtil.setAccentColor(document, value);
              setCompany(company.withAccentColor(value));
            }}
          />
        )}
      </div>

      <p className='text-sm font-medium text-subtitle'>
        <b className='text-black'>{t(TranslationKey.note).singular.upper}: </b>
        {t(TranslationKey.displaySettingsNote).singular.upper}
      </p>

      <div>
        <SaveChangesButton
          onClick={() => updateCompany()}
          className='w-fit'
          loading={companyUpdateLoading}
          disabled={createFileLoading || !hasChanges}
        />
      </div>
    </div>
  );
}
